import React, { memo, useEffect, useMemo, useState } from 'react';
import { Box } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { currentStepSelector, formSummarySelector } from '../../../store/modules/steps/selectors';
import { CustomSelectTypeOptions, FormStep, FormStepProps, StepsState } from '../../../model/global';
import CustomFormField from '../../FormControls/CustomFormField';
import { CustomFormFieldState } from '../../FormControls/CustomFormField/CustomFormField';
import { getFormSummaryField } from '../../utils';
import { getFormControlInitialState } from '../../utils';
import { stepsActions } from '../../../store/modules/steps/reducer';

/** See keys of the fields object in src/wolf/components/organisms/o-heating-form/o-heating-form.data-utils.ts */
// type CustomKeys = 'customerAddress' | 'checkboxes' | 'commissionData' | 'installerData';

const CustomerAddress: React.FC<FormStepProps> = ({ data: { stepTemplate } }) => {

	const dispatch = useDispatch()
	const step = useSelector(currentStepSelector) as FormStep<'checkboxes' | 'commissionData' | 'customerAddress' | 'installerData'>
	const summary = useSelector(formSummarySelector) as StepsState['formSummary']
	const stepErrors = summary.validationErrors[step.stepTemplate]

	const fields: {
		commissionDataCheckbox: CustomFormFieldState | undefined,
		installerDataCheckbox: CustomFormFieldState | undefined
	} = useMemo(
		() => {
			return {
				commissionDataCheckbox: getFormControlInitialState('commission_data_checkbox', step.stepData.fields.checkboxes),
				installerDataCheckbox: getFormControlInitialState('installer_data_checkbox', step.stepData.fields.checkboxes),
			}
		},
		[step.stepData.fields.checkboxes]
	)

	// initialise the step data once
	useEffect(() => {
		// don't re-init fields
		if (summary.initialized[stepTemplate]) return;

		const initialData: Record<string, CustomFormFieldState> = {};

		// fill in state with initial field values and validity
		for (const [key, element] of Object.entries(fields)) {
			if (!element || !element.name) continue;

			const value = summary.steps.CUSTOMER_ADDRESS
			let elementValue = element.value
			if (value[element.name] && value[element.name]?.value) {
				elementValue = value[element.name]?.value
			}

			const invalid = element.value === '' && element.required === true
			initialData[element.name] = {
				name: element.name as string,
				originalField: element.originalField,
				value: elementValue,
				required: element.required,
				valid: !invalid,
				invalidReason: element.invalidReason || '',
			};
		}

		const partners = summary.steps.MAIN_ENQUIRY.target_group?.originalField['options']
		const requiredPartners: Array<string> = []
		if (partners) {
			partners.forEach((partner: CustomSelectTypeOptions) => {
				if (partner.options && partner.options[0].label === 'companyFieldRequired' && partner.options[0].value === 'true') {
					if (typeof partner.value === "string") {
						requiredPartners.push(partner.value)
					}
				}
			})
		}

		// build customer address fields
		step.stepData.fields.customerAddress.forEach(element => {
			if (!element || !element.name) return;
			const item = getFormControlInitialState(element.name, step.stepData.fields.customerAddress);
			if (item && item.name === 'customer_company') {
				item.required = !!(summary.steps.MAIN_ENQUIRY.target_group && requiredPartners.includes(summary.steps.MAIN_ENQUIRY.target_group.value));
			}

			const value = summary.steps.CUSTOMER_ADDRESS
			if (item && value[item.name] && value[item.name]?.value) {
				item.value = value[item.name]?.value
			}

			if (item) initialData[element.name] = item
		})

		// build commission address fields
		step.stepData.fields.commissionData.forEach(element => {

			if (!element || !element.name) return;
			const item = getFormControlInitialState(element.name, step.stepData.fields.commissionData);
			if (item && item.name === 'commission_company') {
				item.required = !!(summary.steps.MAIN_ENQUIRY.target_group && requiredPartners.includes(summary.steps.MAIN_ENQUIRY.target_group.value));
			}

			const value = summary.steps.CUSTOMER_ADDRESS
			if (item && value[item.name] && value[item.name]?.value) {
				item.value = value[item.name]?.value
			}

			if (item) initialData[element.name] = item
		})

		// build installer address fields
		step.stepData.fields.installerData.forEach(element => {
			if (!element || !element.name) return;
			const item = getFormControlInitialState(element.name, step.stepData.fields.installerData);
			if (item) initialData[element.name] = item
		})

		dispatch(stepsActions.initializeStepPayloadValues({
			stepTemplate,
			stepPayload: initialData,
			initialize: true,
		}));

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		dispatch,
		fields,
		step.stepData.fields.commissionData,
		step.stepData.fields.customerAddress,
		step.stepData.fields.installerData,
		stepTemplate,
		summary.initialized,
		summary.steps.MAIN_ENQUIRY.target_group
	])

	const handleCheckboxChange = (event: CustomFormFieldState<boolean>, field?: 'checkbox1' | 'checkbox2') => {
		dispatch(stepsActions.updateStepPayloadValues({
			stepTemplate,
			stepPayload: event,
		}));
		if (!event.checked) return;

		// if the field is checked, populate the 2nd or 3rd form
		// checkbox1 is connected to commissionFields (step.stepData.fields.commissionData)
		// checkbox2 is connected to installerFields (step.stepData.fields.installerData)

		// @todo - refactor this one day?
		if (field === 'checkbox1') {
			const allRelated: CustomFormFieldState[] = [];
			step.stepData.fields.commissionData.forEach(f => {
				const reduxItem = getFormSummaryField(summary, stepTemplate, f.name);
				if (!reduxItem || !reduxItem.originalField.relatedField) return;
				// put the corresponding main address value in the commission address field
				const { relatedField } = reduxItem.originalField
				// get the related field data from the address
				const relatedAddressFormControl = getFormSummaryField(summary, stepTemplate, relatedField);
				if (!relatedAddressFormControl) return;

				// only copy values which are valid
				if (relatedAddressFormControl.valid) {
					const newRelatedItem = JSON.parse(JSON.stringify(reduxItem)) as CustomFormFieldState;
					newRelatedItem.value = relatedAddressFormControl.value
					newRelatedItem.valid = relatedAddressFormControl.valid // also update validity?
					allRelated.push(newRelatedItem)
				}
			})
			dispatch(stepsActions.updateStepPayloadValues({
				stepTemplate,
				stepPayload: allRelated,
			}));

		}
		else if (field === 'checkbox2') {
			const allRelated: CustomFormFieldState[] = [];
			step.stepData.fields.installerData.forEach(f => {
				const reduxItem = getFormSummaryField(summary, stepTemplate, f.name);
				if (!reduxItem || !reduxItem.originalField.relatedField) return;
				// put the corresponding main address value in the installer address field
				const { relatedField } = reduxItem.originalField
				// get the related field data from the address
				const relatedAddressFormControl = getFormSummaryField(summary, stepTemplate, relatedField);
				if (!relatedAddressFormControl) return;

				// only copy values which are valid
				if (relatedAddressFormControl.valid) {
					const newRelatedItem = JSON.parse(JSON.stringify(reduxItem)) as CustomFormFieldState;
					newRelatedItem.value = relatedAddressFormControl.value
					newRelatedItem.valid = relatedAddressFormControl.valid // also update validity?
					allRelated.push(newRelatedItem)
				}
			})
			dispatch(stepsActions.updateStepPayloadValues({
				stepTemplate,
				stepPayload: allRelated,
			}));
		}
	}

	const fieldChangeHandler = (event: CustomFormFieldState): void => {
		dispatch(stepsActions.updateStepPayloadValues({
			stepTemplate,
			stepPayload: event,
		}));
	}

	if (!step) return null

	// get the 2 checkbox fields
	const sumStepTemp = (item: string) => item ? summary.steps[stepTemplate][item] : undefined
	const commissionDataCheckbox = sumStepTemp('commission_data_checkbox') ? sumStepTemp('commission_data_checkbox') : undefined;
	const installerDataCheckbox = sumStepTemp('installer_data_checkbox') ? sumStepTemp('installer_data_checkbox') : undefined;

	// get redux main address fields
	const addressFields: CustomFormFieldState<any>[] = []
	step.stepData.fields.customerAddress.forEach(f => {
		if (!f.name) return;
		if (Object.keys(summary.steps[stepTemplate]).length === 0) return;
		const item = getFormSummaryField(summary, stepTemplate, f.name);
		item && addressFields.push({ ...item, required: item?.originalField?.required || false })
	})

	const commissionFields: CustomFormFieldState<any>[] = []
	step.stepData.fields.commissionData.forEach(f => {
		if (!f.name) return;
		if (Object.keys(summary.steps[stepTemplate]).length === 0) return;
		const item = getFormSummaryField(summary, stepTemplate, f.name);
		item && commissionFields.push({ ...item, required: item?.originalField?.required || false })
	})

	const installerFields: CustomFormFieldState<any>[] = []
	step.stepData.fields.installerData.forEach(f => {
		if (!f.name) return;
		if (Object.keys(summary.steps[stepTemplate]).length === 0) return;
		const item = getFormSummaryField(summary, stepTemplate, f.name);
		item && installerFields.push({ ...item, required: item?.originalField?.required || false })
	})

	const attributeOBJ = (field: any) => { return ({ field, fieldErrors: stepErrors }) }

	return (
		<React.Fragment>
			<Box>
				<fieldset className={'customer-address-fieldset'}>
					<h2 className={'customer-address-fieldset-title'}>{step.stepData.fieldTranslations.customerAddress}</h2>
					<div className="uk-grid-medium uk-grid uk-grid-stack">
						{/* 1. ALL Customer Address fields */}
						{addressFields?.map((field, key) => {
							return <CustomFormField key={`comission-${key}`} {...attributeOBJ(field)} onFieldChange={e => fieldChangeHandler(e)} />
						})}

						{/* CHECKBOX 1 */}
						{commissionDataCheckbox && <div className={'checkbox-holder'}>
							<CustomFormField {...attributeOBJ(commissionDataCheckbox)} onFieldChange={e => handleCheckboxChange(e, 'checkbox1')} />
						</div>}
						{/* CHECKBOX 2 */}
						{installerDataCheckbox && <div className={'checkbox-holder'}>
							<CustomFormField {...attributeOBJ(installerDataCheckbox)} onFieldChange={e => handleCheckboxChange(e, 'checkbox2')} />
						</div>}
					</div>
				</fieldset>
			</Box>

			<Box>
				<fieldset className={'customer-address-fieldset'}>
					<h2 className={'customer-address-fieldset-title'}>{step.stepData.fieldTranslations.commissionData}</h2>
					<div className="uk-grid-medium uk-grid uk-grid-stack">
						{/* 2. Commission Data (connected to checkbox 1) */}
						{commissionFields?.map((field, key) => {
							return <CustomFormField key={`comission-${key}`} {...attributeOBJ(field)} onFieldChange={e => fieldChangeHandler(e)} />
						})}
					</div>
				</fieldset>
			</Box>

			<Box>
				<fieldset className={'customer-address-fieldset'}>
					<h2 className={'customer-address-fieldset-title'}>{step.stepData.fieldTranslations.installerData}</h2>
					<div className="uk-grid-medium uk-grid uk-grid-stack">
						{/* 3. Installer Data (connected to checkbox 2) */}
						{installerFields?.map((field, key) => {
							return <CustomFormField key={`installer-${key}`} {...attributeOBJ(field)} onFieldChange={e => fieldChangeHandler(e)} />
						})}
					</div>
				</fieldset>
			</Box>
		</React.Fragment>
	)
}
export default memo(CustomerAddress)
